import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, LinearProgress, Modal, Slider, Stack, Switch, TextField, Typography,
} from '@mui/material';
import {
  ArrowBack, AutoFixHigh, ChevronLeft, ChevronRight, Close, ContentCut, Crop, Add, PhotoCamera,
  PhotoLibrary, RotateRight, Send, Speed, TextFields, Title, Tune, Videocam, VolumeUp,
} from '@mui/icons-material';
import { db, auth, storage } from './firebase';
import { collection, addDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { useTranslatedDictionary } from './hooks/useTranslatedDictionary';
import { PostDetailsFields, usePostDetails } from './PostDetails';
import {
  buildFilterCss, DEFAULT_ADJUST, REEL_FILTERS, REEL_SPEEDS, REEL_TEXT_COLORS, ReelOverlayText, resolveVideoDuration, useVideoAudioRefresh,
} from './reelEdit';
import { canvasToJpeg, loadImage, OVERLAY_FONT_RATIO, renderImage } from './imageEdit';
import CameraCapture, { useShowCameraOptions } from './CameraCapture';

// Mismos límites que storage.rules
const MAX_IMAGE_BYTES = 20 * 1024 * 1024;
const MAX_VIDEO_BYTES = 200 * 1024 * 1024;
const MAX_ITEMS = 10;
const MAX_OVERLAYS = 5;
const MAX_TEXT = 5000;
const MIN_TRIM_SECONDS = 1;
const PREVIEW_MAX_SIDE = 1080;

const PUBLICAR_TEXT = {
  es: {
    newPost: 'Nueva publicación',
    chooseTitle: '¿Qué quieres publicar?',
    galleryOption: 'Fotos y videos',
    galleryOptionDesc: 'De la galería de tu teléfono o tu computadora',
    photoOption: 'Tomar foto',
    photoOptionDesc: 'Usa la cámara de tu dispositivo',
    videoOption: 'Grabar video',
    videoOptionDesc: 'Usa la cámara de tu dispositivo',
    textOption: 'Solo texto',
    textOptionDesc: 'Escribe algo y elige un fondo',
    placeholder: '¿Qué está pasando en tu línea?',
    captionPlaceholder: 'Escribe una descripción...',
    textBackground: 'Fondo del texto',
    preview: 'Vista previa',
    noBackground: 'Sin fondo',
    next: 'Siguiente',
    details: 'Detalles',
    publish: 'Publicar',
    publishing: 'Publicando',
    uploadError: 'No se pudo publicar. Intenta de nuevo.',
    tooManyFiles: 'Puedes subir hasta 10 archivos.',
    unsupportedFile: 'Ese archivo no es una foto ni un video.',
    imageTooBig: 'Una foto supera el máximo de 20 MB.',
    videoTooBig: 'Un video supera el máximo de 200 MB.',
    toolCrop: 'Recortar',
    toolRotate: 'Girar',
    toolTrim: 'Recortar',
    toolSpeed: 'Velocidad',
    toolFilters: 'Filtros',
    toolAdjust: 'Ajustes',
    toolText: 'Texto',
    toolSound: 'Sonido',
    aspectOriginal: 'Original',
    rotateRight: 'Girar 90°',
    brightness: 'Brillo',
    contrast: 'Contraste',
    saturation: 'Saturación',
    resetAdjust: 'Restablecer',
    trimRange: 'Duración',
    trimUnavailable: 'No se puede recortar este video porque no informa su duración.',
    originalSound: 'Sonido original',
    textPlaceholder: 'Escribe un texto...',
    addText: 'Agregar',
    textHint: 'Arrastra el texto sobre la imagen para ubicarlo.',
    filterNone: 'Normal',
    filterVivid: 'Vivo',
    filterWarm: 'Cálido',
    filterCool: 'Frío',
    filterBw: 'B/N',
    filterRetro: 'Retro',
    filterDrama: 'Drama',
    bgOcean: 'Océano',
    bgSunset: 'Atardecer',
    bgViolet: 'Violeta',
    bgMidnight: 'Medianoche',
    bgLime: 'Lima',
    discardTitle: '¿Descartar esta publicación?',
    discardWarning: 'Se perderán los cambios que hiciste.',
    keepEditing: 'Seguir editando',
    discard: 'Descartar',
  },
  en: {
    newPost: 'New post',
    chooseTitle: 'What do you want to post?',
    galleryOption: 'Photos and videos',
    galleryOptionDesc: 'From your phone gallery or your computer',
    photoOption: 'Take a photo',
    photoOptionDesc: "Use your device's camera",
    videoOption: 'Record video',
    videoOptionDesc: "Use your device's camera",
    textOption: 'Text only',
    textOptionDesc: 'Write something and choose a background',
    placeholder: 'What is happening in your feed?',
    captionPlaceholder: 'Write a description...',
    textBackground: 'Text background',
    preview: 'Preview',
    noBackground: 'No background',
    next: 'Next',
    details: 'Details',
    publish: 'Post',
    publishing: 'Posting',
    uploadError: 'Could not publish. Please try again.',
    tooManyFiles: 'You can upload up to 10 files.',
    unsupportedFile: 'That file is not a photo or a video.',
    imageTooBig: 'A photo is over the 20 MB limit.',
    videoTooBig: 'A video is over the 200 MB limit.',
    toolCrop: 'Crop',
    toolRotate: 'Rotate',
    toolTrim: 'Trim',
    toolSpeed: 'Speed',
    toolFilters: 'Filters',
    toolAdjust: 'Adjust',
    toolText: 'Text',
    toolSound: 'Sound',
    aspectOriginal: 'Original',
    rotateRight: 'Rotate 90°',
    brightness: 'Brightness',
    contrast: 'Contrast',
    saturation: 'Saturation',
    resetAdjust: 'Reset',
    trimRange: 'Length',
    trimUnavailable: "This video can't be trimmed because it doesn't report its length.",
    originalSound: 'Original sound',
    textPlaceholder: 'Type some text...',
    addText: 'Add',
    textHint: 'Drag the text on the image to place it.',
    filterNone: 'Normal',
    filterVivid: 'Vivid',
    filterWarm: 'Warm',
    filterCool: 'Cool',
    filterBw: 'B&W',
    filterRetro: 'Retro',
    filterDrama: 'Drama',
    bgOcean: 'Ocean',
    bgSunset: 'Sunset',
    bgViolet: 'Violet',
    bgMidnight: 'Midnight',
    bgLime: 'Lime',
    discardTitle: 'Discard this post?',
    discardWarning: 'Your changes will be lost.',
    keepEditing: 'Keep editing',
    discard: 'Discard',
  },
};

const TEXT_BACKGROUND_OPTIONS = [
  { id: 'none', background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.08)' },
  { id: 'ocean', labelKey: 'bgOcean', background: 'linear-gradient(135deg, rgba(0,212,255,0.26) 0%, rgba(0,69,255,0.38) 100%)', color: '#effcff', border: '1px solid rgba(0,212,255,0.24)' },
  { id: 'sunset', labelKey: 'bgSunset', background: 'linear-gradient(135deg, rgba(255,128,0,0.32) 0%, rgba(255,69,96,0.34) 100%)', color: '#fff6ef', border: '1px solid rgba(255,140,80,0.24)' },
  { id: 'violet', labelKey: 'bgViolet', background: 'linear-gradient(135deg, rgba(126,87,255,0.32) 0%, rgba(64,196,255,0.24) 100%)', color: '#f7f3ff', border: '1px solid rgba(126,87,255,0.24)' },
  { id: 'midnight', labelKey: 'bgMidnight', background: 'linear-gradient(135deg, rgba(18,23,35,0.96) 0%, rgba(41,54,76,0.96) 100%)', color: '#ffffff', border: '1px solid rgba(255,255,255,0.08)' },
  { id: 'lime', labelKey: 'bgLime', background: 'linear-gradient(135deg, rgba(103,220,106,0.32) 0%, rgba(0,212,255,0.22) 100%)', color: '#f4fff4', border: '1px solid rgba(103,220,106,0.24)' },
];

const ASPECTS = [
  { id: 'original', value: null },
  { id: '1:1', value: 1 },
  { id: '4:5', value: 4 / 5 },
  { id: '16:9', value: 16 / 9 },
];

const IMAGE_TOOLS = [
  { id: 'crop', labelKey: 'toolCrop', Icon: Crop },
  { id: 'rotate', labelKey: 'toolRotate', Icon: RotateRight },
  { id: 'filters', labelKey: 'toolFilters', Icon: AutoFixHigh },
  { id: 'adjust', labelKey: 'toolAdjust', Icon: Tune },
  { id: 'text', labelKey: 'toolText', Icon: TextFields },
];
const VIDEO_TOOLS = [
  { id: 'trim', labelKey: 'toolTrim', Icon: ContentCut },
  { id: 'speed', labelKey: 'toolSpeed', Icon: Speed },
  { id: 'filters', labelKey: 'toolFilters', Icon: AutoFixHigh },
  { id: 'adjust', labelKey: 'toolAdjust', Icon: Tune },
  { id: 'text', labelKey: 'toolText', Icon: TextFields },
  { id: 'sound', labelKey: 'toolSound', Icon: VolumeUp },
];

const SLIDER_SX = { color: '#00d4ff' };
const CHIP_SX = (selected) => ({
  flexShrink: 0,
  fontWeight: 600,
  color: selected ? '#041015' : '#fff',
  bgcolor: selected ? '#00d4ff' : 'rgba(255,255,255,0.1)',
  '&:hover': { bgcolor: selected ? '#00d4ff' : 'rgba(255,255,255,0.18)' },
});

const defaultEdit = () => ({
  rotation: 0,
  aspect: null,
  filter: 'none',
  adjust: { ...DEFAULT_ADJUST },
  overlays: [],
  trim: null,
  duration: 0,
  speed: 1,
  muted: false,
});

const formatTime = (seconds) => {
  const total = Math.max(0, Math.round(seconds * 10) / 10);
  const minutes = Math.floor(total / 60);
  return `${minutes}:${(total - minutes * 60).toFixed(1).padStart(4, '0')}`;
};

const isAdjusted = (adjust) => Object.keys(DEFAULT_ADJUST).some((key) => adjust[key] !== DEFAULT_ADJUST[key]);

const imageNeedsRender = (edit) =>
  edit.rotation !== 0 || edit.aspect !== null || edit.filter !== 'none' || isAdjusted(edit.adjust) || edit.overlays.length > 0;

const videoEditPayload = (edit) => {
  const trimmedEnd = edit.trim && edit.duration && edit.trim[1] < edit.duration - 0.05;
  return {
    trimStart: edit.trim ? edit.trim[0] : 0,
    trimEnd: trimmedEnd ? edit.trim[1] : null,
    speed: edit.speed,
    filter: edit.filter,
    adjust: edit.adjust,
    muted: edit.muted,
    overlays: edit.overlays,
  };
};

const Publicar = ({ open, handleClose, onSuccess, people = [], language = 'es', themeMode = 'oscuro' }) => {
  const t = useTranslatedDictionary(PUBLICAR_TEXT, language);
  const isLightTheme = themeMode === 'claro';
  const onSurface = (opacity = 1) =>
    isLightTheme ? `rgba(15,20,32,${opacity})` : `rgba(255,255,255,${opacity})`;
  const user = auth.currentUser;
  const details = usePostDetails({ user, people, language });
  const showCameraOptions = useShowCameraOptions();

  const [step, setStep] = useState('choose');
  const [mode, setMode] = useState('media');
  const [items, setItems] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [activeTool, setActiveTool] = useState('filters');
  const [text, setText] = useState('');
  const [textBackground, setTextBackground] = useState('none');
  const [draftText, setDraftText] = useState('');
  const [textColor, setTextColor] = useState(REEL_TEXT_COLORS[0]);
  const [basePreview, setBasePreview] = useState(null);
  const [videoDims, setVideoDims] = useState(null);
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  const [userPhoto, setUserPhoto] = useState(user?.photoURL || '');
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [errorMsg, setErrorMsg] = useState('');
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [cameraMode, setCameraMode] = useState(null);

  const galleryInputRef = useRef(null);
  const stageRef = useRef(null);
  const frameRef = useRef(null);
  const videoRef = useRef(null);
  const draggingIdRef = useRef(null);
  const draggingTrimRef = useRef(false);
  const imagesRef = useRef(new Map());
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const current = items.find((item) => item.id === currentId) || items[0] || null;
  const edit = current?.edit;
  const tools = current?.type === 'video' ? VIDEO_TOOLS : IMAGE_TOOLS;
  const tool = tools.some((option) => option.id === activeTool) ? activeTool : tools[0].id;
  const previewFilter = edit ? buildFilterCss(edit.filter, edit.adjust) : 'none';
  useVideoAudioRefresh(videoRef, current?.type === 'video' ? current.id : null, Boolean(edit?.muted));
  const selectedBackground = TEXT_BACKGROUND_OPTIONS.find((option) => option.id === textBackground) || TEXT_BACKGROUND_OPTIONS[0];
  const backgroundTextColor = textBackground === 'none' ? onSurface(1) : selectedBackground.color;

  const overallProgress = items.length
    ? Math.round(items.reduce((sum, item) => sum + (uploadProgress[item.id] || 0), 0) / items.length)
    : 0;
  const canPublish = mode === 'media'
    ? items.length > 0
    : Boolean(text.trim()) || details.hashtags.length > 0;
  const isDirty = items.length > 0 || Boolean(text.trim());

  useEffect(() => () => {
    itemsRef.current.forEach((item) => URL.revokeObjectURL(item.preview));
  }, []);

  useEffect(() => {
    if (!user) return;
    getDoc(doc(db, 'users', user.uid)).then((snap) => {
      if (snap.exists() && snap.data().photoURL) setUserPhoto(snap.data().photoURL);
    }).catch(() => {});
  }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mide el área disponible para ajustar la imagen sin deformar sus proporciones.
  const hasItems = items.length > 0;
  useEffect(() => {
    const node = stageRef.current;
    if (!node) return undefined;
    const observer = new ResizeObserver(([entry]) => {
      setStageSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [step, mode, hasItems]);

  // La vista previa de la foto se regenera solo al girar o recortar; filtros y texto van por CSS.
  const currentImageId = current?.type === 'image' ? current.id : null;
  const rotation = edit?.rotation;
  const aspect = edit?.aspect;
  useEffect(() => {
    if (!currentImageId) {
      setBasePreview(null);
      return undefined;
    }
    let cancelled = false;
    const item = itemsRef.current.find((candidate) => candidate.id === currentImageId);
    const cached = imagesRef.current.get(currentImageId);
    (cached ? Promise.resolve(cached) : loadImage(item.preview))
      .then((image) => {
        imagesRef.current.set(currentImageId, image);
        if (cancelled) return;
        const canvas = renderImage(image, { rotation, aspect, maxSide: PREVIEW_MAX_SIDE });
        setBasePreview({ url: canvas.toDataURL('image/jpeg', 0.9), width: canvas.width, height: canvas.height });
      })
      .catch(() => {
        if (!cancelled) setErrorMsg(t('unsupportedFile'));
      });
    return () => { cancelled = true; };
  }, [currentImageId, rotation, aspect]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (videoRef.current && current?.type === 'video') videoRef.current.playbackRate = edit.speed;
  }, [current?.type, edit?.speed]);

  useEffect(() => {
    setVideoDims(null);
  }, [current?.id]);

  const updateEdit = (patch) => {
    if (!current) return;
    setItems((prev) => prev.map((item) => (
      item.id === current.id ? { ...item, edit: { ...item.edit, ...patch } } : item
    )));
  };

  const handleFiles = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    addFiles(files);
  };

  const addFiles = (files) => {
    if (!files.length) return;
    if (items.length + files.length > MAX_ITEMS) {
      setErrorMsg(t('tooManyFiles'));
      return;
    }
    for (const file of files) {
      const isVideo = file.type.startsWith('video/');
      if (!isVideo && !file.type.startsWith('image/')) {
        setErrorMsg(t('unsupportedFile'));
        return;
      }
      if (isVideo ? file.size >= MAX_VIDEO_BYTES : file.size >= MAX_IMAGE_BYTES) {
        setErrorMsg(t(isVideo ? 'videoTooBig' : 'imageTooBig'));
        return;
      }
    }
    const newItems = files.map((file) => ({
      id: `${file.name}_${file.lastModified}_${Math.random().toString(36).slice(2)}`,
      file,
      preview: URL.createObjectURL(file),
      type: file.type.startsWith('video/') ? 'video' : 'image',
      edit: defaultEdit(),
    }));
    setErrorMsg('');
    setItems((prev) => [...prev, ...newItems]);
    setCurrentId(newItems[0].id);
    setMode('media');
    setStep('edit');
  };

  const removeItem = (id) => {
    const remaining = items.filter((item) => item.id !== id);
    const removed = items.find((item) => item.id === id);
    if (removed) URL.revokeObjectURL(removed.preview);
    imagesRef.current.delete(id);
    setItems(remaining);
    if (currentId === id) setCurrentId(remaining[0]?.id || null);
    if (remaining.length === 0) setStep('choose');
  };

  const moveItem = (id, direction) => {
    setItems((prev) => {
      const index = prev.findIndex((item) => item.id === id);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const requestClose = () => {
    if (loading) return;
    if (isDirty) setConfirmDiscard(true);
    else handleClose();
  };

  const goBack = () => {
    if (step === 'details') setStep('edit');
    else setStep('choose');
  };

  // --- Video
  const handleVideoMetadata = async () => {
    const video = videoRef.current;
    if (!video || !current) return;
    setVideoDims({ width: video.videoWidth, height: video.videoHeight });
    const duration = await resolveVideoDuration(video);
    if (videoRef.current !== video) return;
    if (!edit.trim && duration) updateEdit({ duration, trim: [0, duration] });
    video.playbackRate = edit.speed;
    if (edit.trim) video.currentTime = edit.trim[0];
    video.play().catch(() => {});
  };

  const handleVideoTime = () => {
    const video = videoRef.current;
    if (!video || !edit?.trim || draggingTrimRef.current) return;
    if (video.currentTime >= edit.trim[1] || video.currentTime < edit.trim[0]) {
      video.currentTime = edit.trim[0];
    }
  };

  const handleTrimChange = (_, value, activeThumb) => {
    if (value[1] - value[0] < MIN_TRIM_SECONDS) return;
    draggingTrimRef.current = true;
    videoRef.current?.pause();
    updateEdit({ trim: value });
    if (videoRef.current) videoRef.current.currentTime = activeThumb === 0 ? value[0] : value[1];
  };

  const handleTrimCommitted = () => {
    draggingTrimRef.current = false;
    if (videoRef.current && edit?.trim) {
      videoRef.current.currentTime = edit.trim[0];
      videoRef.current.play().catch(() => {});
    }
  };

  // --- Texto sobre la imagen
  const addOverlay = () => {
    const value = draftText.trim();
    if (!value || edit.overlays.length >= MAX_OVERLAYS) return;
    updateEdit({ overlays: [...edit.overlays, { id: `${Date.now()}`, text: value, x: 50, y: 40, color: textColor }] });
    setDraftText('');
  };

  const handleOverlayPointerDown = (e, id) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    draggingIdRef.current = id;
  };

  const handleOverlayPointerMove = (e) => {
    const id = draggingIdRef.current;
    const rect = frameRef.current?.getBoundingClientRect();
    if (!id || !rect) return;
    const x = Math.min(100, Math.max(0, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((e.clientY - rect.top) / rect.height) * 100));
    updateEdit({ overlays: edit.overlays.map((overlay) => (overlay.id === id ? { ...overlay, x, y } : overlay)) });
  };

  const handleOverlayPointerUp = () => {
    draggingIdRef.current = null;
  };

  // --- Publicación
  const prepareUpload = async (item) => {
    if (item.type !== 'image' || !imageNeedsRender(item.edit)) {
      return { blob: item.file, name: item.file.name, contentType: item.file.type };
    }
    const image = imagesRef.current.get(item.id) || await loadImage(item.preview);
    const canvas = renderImage(image, {
      rotation: item.edit.rotation,
      aspect: item.edit.aspect,
      filterCss: buildFilterCss(item.edit.filter, item.edit.adjust),
      overlays: item.edit.overlays,
    });
    return { blob: await canvasToJpeg(canvas), name: `${item.file.name}.jpg`, contentType: 'image/jpeg' };
  };

  const uploadItem = async (item) => {
    const { blob, name, contentType } = await prepareUpload(item);
    const url = await new Promise((resolve, reject) => {
      const task = uploadBytesResumable(
        ref(storage, `posts/${user.uid}/${Date.now()}_${name}`),
        blob,
        { contentType },
      );
      task.on(
        'state_changed',
        (snapshot) => {
          const pct = snapshot.totalBytes ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100) : 0;
          setUploadProgress((prev) => ({ ...prev, [item.id]: pct }));
        },
        reject,
        () => getDownloadURL(task.snapshot.ref).then(resolve, reject),
      );
    });
    return {
      type: item.type,
      url,
      name: item.file.name,
      ...(item.type === 'video' ? { edit: videoEditPayload(item.edit) } : {}),
    };
  };

  const handlePost = async () => {
    if (!canPublish || !user || loading) return;
    const detailsError = details.validate();
    if (detailsError) {
      setErrorMsg(detailsError);
      return;
    }
    setLoading(true);
    setErrorMsg('');
    setUploadProgress({});

    try {
      let username = user.displayName || '';
      let userImg = user.photoURL || '';
      let handle = '';
      try {
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        if (userSnap.exists()) {
          const data = userSnap.data();
          username = data.displayName || username;
          userImg = data.photoURL || userImg;
          handle = data.username || '';
        }
      } catch {
        // Si falla la lectura se usan los datos de la sesión.
      }
      const emailName = user.email?.split('@')[0] || 'usuario';

      await details.saveCloseFriends();
      const media = mode === 'media' ? await Promise.all(items.map(uploadItem)) : [];

      await addDoc(collection(db, 'posts'), {
        uid: user.uid,
        username: username || emailName,
        handle: handle || emailName,
        userImg,
        text: details.withHashtags(text),
        textBackground: mode === 'text' ? textBackground : 'none',
        ...details.fields(),
        media,
        imageUrl: media.find((item) => item.type === 'image')?.url || '',
        videoUrl: media.find((item) => item.type === 'video')?.url || '',
        likes: [],
        savedBy: [],
        commentsCount: 0,
        timestamp: serverTimestamp(),
      });
      onSuccess?.();
    } catch (error) {
      console.error('Error al publicar:', error);
      setErrorMsg(t('uploadError'));
      setLoading(false);
    }
  };

  // --- Tamaño de la imagen dentro del área de edición
  const naturalDims = current?.type === 'image' ? basePreview : videoDims;
  const fit = naturalDims && stageSize.width && stageSize.height
    ? Math.min(stageSize.width / naturalDims.width, stageSize.height / naturalDims.height)
    : 0;
  const frameWidth = fit ? naturalDims.width * fit : 0;
  const frameHeight = fit ? naturalDims.height * fit : 0;

  const toolPanels = {
    crop: (
      <Stack direction="row" spacing={1} justifyContent="center">
        {ASPECTS.map((option) => (
          <Chip
            key={option.id}
            label={option.value ? option.id : t('aspectOriginal')}
            onClick={() => updateEdit({ aspect: option.value })}
            sx={CHIP_SX(edit?.aspect === option.value)}
          />
        ))}
      </Stack>
    ),
    rotate: (
      <Stack alignItems="center">
        <Button
          startIcon={<RotateRight />}
          onClick={() => updateEdit({ rotation: (edit.rotation + 90) % 360 })}
          sx={{ color: '#00d4ff', textTransform: 'none', fontWeight: 700 }}
        >
          {t('rotateRight')}
        </Button>
      </Stack>
    ),
    filters: (
      <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 0.5, scrollbarWidth: 'none' }}>
        {REEL_FILTERS.map((option) => (
          <Chip key={option.id} label={t(option.labelKey)} onClick={() => updateEdit({ filter: option.id })} sx={CHIP_SX(edit?.filter === option.id)} />
        ))}
      </Stack>
    ),
    adjust: edit && (
      <Stack sx={{ px: 1 }}>
        {Object.keys(DEFAULT_ADJUST).map((key) => (
          <Stack key={key} direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="caption" sx={{ width: 76, flexShrink: 0 }}>{t(key)}</Typography>
            <Slider
              size="small"
              min={50}
              max={150}
              value={edit.adjust[key]}
              onChange={(_, value) => updateEdit({ adjust: { ...edit.adjust, [key]: value } })}
              sx={SLIDER_SX}
            />
          </Stack>
        ))}
        {isAdjusted(edit.adjust) && (
          <Button size="small" onClick={() => updateEdit({ adjust: { ...DEFAULT_ADJUST } })} sx={{ color: '#00d4ff', textTransform: 'none', alignSelf: 'flex-end' }}>
            {t('resetAdjust')}
          </Button>
        )}
      </Stack>
    ),
    text: edit && (
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            fullWidth
            size="small"
            variant="standard"
            placeholder={t('textPlaceholder')}
            value={draftText}
            onChange={(e) => setDraftText(e.target.value.slice(0, 100))}
            InputProps={{ disableUnderline: true, sx: { color: '#fff' } }}
          />
          <Button
            onClick={addOverlay}
            disabled={!draftText.trim() || edit.overlays.length >= MAX_OVERLAYS}
            sx={{ color: '#00d4ff', textTransform: 'none', fontWeight: 700 }}
          >
            {t('addText')}
          </Button>
        </Stack>
        <Stack direction="row" spacing={1}>
          {REEL_TEXT_COLORS.map((color) => (
            <Box
              key={color}
              component="button"
              type="button"
              aria-label={color}
              onClick={() => setTextColor(color)}
              sx={{ width: 24, height: 24, p: 0, borderRadius: '50%', cursor: 'pointer', bgcolor: color, border: textColor === color ? '2px solid #00d4ff' : '2px solid rgba(255,255,255,0.35)' }}
            />
          ))}
        </Stack>
        {edit.overlays.length > 0 && (
          <>
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
              {edit.overlays.map((overlay) => (
                <Chip
                  key={overlay.id}
                  size="small"
                  label={overlay.text}
                  onDelete={() => updateEdit({ overlays: edit.overlays.filter((candidate) => candidate.id !== overlay.id) })}
                  sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.1)', maxWidth: 180, '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)' } }}
                />
              ))}
            </Stack>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)' }}>{t('textHint')}</Typography>
          </>
        )}
      </Stack>
    ),
    trim: edit?.trim ? (
      <Box sx={{ px: 1.5 }}>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="caption">{t('trimRange')}: {formatTime((edit.trim[1] - edit.trim[0]) / edit.speed)}</Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)' }}>
            {formatTime(edit.trim[0])} - {formatTime(edit.trim[1])}
          </Typography>
        </Stack>
        <Slider value={edit.trim} min={0} max={edit.duration} step={0.1} disableSwap onChange={handleTrimChange} onChangeCommitted={handleTrimCommitted} sx={SLIDER_SX} />
      </Box>
    ) : (
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', px: 1 }}>{t('trimUnavailable')}</Typography>
    ),
    speed: (
      <Stack direction="row" spacing={1} justifyContent="center">
        {REEL_SPEEDS.map((value) => (
          <Chip key={value} label={`${value}x`} onClick={() => updateEdit({ speed: value })} sx={CHIP_SX(edit?.speed === value)} />
        ))}
      </Stack>
    ),
    sound: (
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1 }}>
        <Typography variant="body2">{t('originalSound')}</Typography>
        <Switch
          checked={!edit?.muted}
          onChange={(e) => updateEdit({ muted: !e.target.checked })}
          sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#00d4ff' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#00d4ff' } }}
        />
      </Stack>
    ),
  };

  const chooseOptions = [
    { id: 'gallery', Icon: PhotoLibrary, title: t('galleryOption'), desc: t('galleryOptionDesc'), onClick: () => galleryInputRef.current?.click() },
    ...(showCameraOptions ? [
      { id: 'photo', Icon: PhotoCamera, title: t('photoOption'), desc: t('photoOptionDesc'), onClick: () => setCameraMode('photo') },
      { id: 'video', Icon: Videocam, title: t('videoOption'), desc: t('videoOptionDesc'), onClick: () => setCameraMode('video') },
    ] : []),
    { id: 'text', Icon: Title, title: t('textOption'), desc: t('textOptionDesc'), onClick: () => { setMode('text'); setStep('edit'); } },
  ];

  const isMediaEditor = step === 'edit' && mode === 'media' && current;
  const screenBg = isMediaEditor ? '#000' : (isLightTheme ? '#fff' : '#101010');
  const headerColor = isMediaEditor ? '#fff' : onSurface(1);
  const title = step === 'details' ? t('details') : t('newPost');

  return (
    <Modal open={Boolean(open)} onClose={requestClose} hideBackdrop>
      <Box sx={{ position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column', bgcolor: screenBg, outline: 'none' }}>
        {/* Encabezado */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1, py: 0.5, flexShrink: 0, width: '100%', maxWidth: 600, mx: 'auto', boxSizing: 'border-box' }}>
          {step === 'choose' ? (
            <IconButton onClick={requestClose} sx={{ color: headerColor }}><Close /></IconButton>
          ) : (
            <IconButton disabled={loading} onClick={goBack} sx={{ color: headerColor }}><ArrowBack /></IconButton>
          )}
          <Typography variant="h6" sx={{ color: headerColor, fontWeight: 700 }}>{title}</Typography>
          {step === 'edit' ? (
            <Button
              onClick={() => setStep('details')}
              disabled={mode === 'text' && !text.trim()}
              sx={{ color: '#00d4ff', textTransform: 'none', fontWeight: 700 }}
            >
              {t('next')}
            </Button>
          ) : (
            <Box sx={{ width: 40 }} />
          )}
        </Stack>

        {errorMsg && (
          <Alert severity="error" variant="filled" onClose={() => setErrorMsg('')} sx={{ mx: 1, mb: 1, borderRadius: '12px', flexShrink: 0 }}>
            {errorMsg}
          </Alert>
        )}

        <input ref={galleryInputRef} type="file" hidden accept="image/*,video/*" multiple onChange={handleFiles} />
        {cameraMode && (
          <CameraCapture
            mode={cameraMode}
            language={language}
            onClose={() => setCameraMode(null)}
            onCapture={(file) => {
              setCameraMode(null);
              addFiles([file]);
            }}
          />
        )}

        {/* Paso 1: elegir */}
        {step === 'choose' && (
          <Stack spacing={1.25} sx={{ flex: 1, overflowY: 'auto', boxSizing: 'border-box', width: '100%', maxWidth: 600, mx: 'auto', px: 2, py: 1 }}>
            <Typography variant="h6" sx={{ color: onSurface(1), fontWeight: 700, mb: 0.5 }}>{t('chooseTitle')}</Typography>
            {chooseOptions.map(({ id, Icon, title: optionTitle, desc, onClick }) => (
              <Button
                key={id}
                onClick={onClick}
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  gap: 1.5,
                  p: 1.5,
                  textAlign: 'left',
                  textTransform: 'none',
                  borderRadius: '16px',
                  color: onSurface(1),
                  bgcolor: onSurface(0.04),
                  border: `1px solid ${onSurface(0.08)}`,
                  '&:hover': { borderColor: '#00d4ff', bgcolor: 'rgba(0,212,255,0.08)' },
                }}
              >
                <Box sx={{ width: 44, height: 44, flexShrink: 0, borderRadius: '12px', bgcolor: 'rgba(0,212,255,0.14)', display: 'grid', placeItems: 'center' }}>
                  <Icon sx={{ color: '#00d4ff' }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700 }}>{optionTitle}</Typography>
                  <Typography variant="body2" sx={{ color: onSurface(0.65) }}>{desc}</Typography>
                </Box>
              </Button>
            ))}
          </Stack>
        )}

        {/* Paso 2: editar solo texto */}
        {step === 'edit' && mode === 'text' && (
          <Box sx={{ flex: 1, overflowY: 'auto', boxSizing: 'border-box', width: '100%', maxWidth: 600, mx: 'auto', px: 2, py: 1 }}>
            <Stack direction="row" spacing={2} mb={2}>
              <Avatar src={userPhoto} />
              <TextField
                fullWidth
                multiline
                minRows={4}
                autoFocus
                placeholder={t('placeholder')}
                variant="standard"
                value={text}
                onChange={(e) => setText(e.target.value.slice(0, MAX_TEXT))}
                InputProps={{ disableUnderline: true, sx: { color: onSurface(1), fontSize: '1.1rem' } }}
              />
            </Stack>
            <Typography sx={{ color: onSurface(0.74), fontSize: '0.9rem', fontWeight: 600, mb: 1 }}>{t('textBackground')}</Typography>
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {TEXT_BACKGROUND_OPTIONS.map((option) => {
                const isSelected = option.id === textBackground;
                return (
                  <Button
                    key={option.id}
                    onClick={() => setTextBackground(option.id)}
                    size="small"
                    sx={{
                      minWidth: 0,
                      textTransform: 'none',
                      borderRadius: '999px',
                      px: 1.5,
                      py: 0.8,
                      color: onSurface(1),
                      border: isSelected ? '1px solid #00d4ff' : `1px solid ${onSurface(0.12)}`,
                      boxShadow: isSelected ? '0 0 0 1px #00d4ff' : 'none',
                      background: isSelected && option.id !== 'none' ? option.background : onSurface(0.02),
                    }}
                  >
                    {option.id === 'none' ? t('noBackground') : t(option.labelKey)}
                  </Button>
                );
              })}
            </Stack>
            {text.trim() && (
              <>
                <Typography sx={{ color: onSurface(0.56), fontSize: '0.78rem', mb: 0.75 }}>{t('preview')}</Typography>
                <Box sx={{ borderRadius: '18px', p: 2, minHeight: 110, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', background: selectedBackground.background, border: textBackground === 'none' ? `1px solid ${onSurface(0.12)}` : selectedBackground.border }}>
                  <Typography sx={{ color: backgroundTextColor, fontWeight: 600, whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.55 }}>{text}</Typography>
                </Box>
              </>
            )}
          </Box>
        )}

        {/* Paso 2: editar foto o video */}
        {isMediaEditor && (
          <>
            <Box ref={stageRef} sx={{ flex: 1, minHeight: 0, width: '100%', maxWidth: 600, mx: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              <Box ref={frameRef} sx={{ position: 'relative', overflow: 'hidden', flexShrink: 0, width: frameWidth || '100%', height: frameHeight || '100%' }}>
                {current.type === 'image' ? (
                  basePreview && (
                    <Box component="img" src={basePreview.url} alt="" sx={{ width: '100%', height: '100%', display: 'block', filter: previewFilter }} />
                  )
                ) : (
                  <Box
                    component="video"
                    key={current.id}
                    ref={videoRef}
                    src={current.preview}
                    playsInline
                    muted={edit.muted}
                    onLoadedMetadata={handleVideoMetadata}
                    onTimeUpdate={handleVideoTime}
                    onEnded={() => {
                      if (!videoRef.current) return;
                      videoRef.current.currentTime = edit.trim?.[0] || 0;
                      videoRef.current.play().catch(() => {});
                    }}
                    sx={{ width: '100%', height: '100%', display: 'block', objectFit: 'contain', filter: previewFilter }}
                  />
                )}
                {edit.overlays.map((overlay) => (
                  <ReelOverlayText
                    key={overlay.id}
                    overlay={overlay}
                    onPointerDown={(e) => handleOverlayPointerDown(e, overlay.id)}
                    onPointerMove={handleOverlayPointerMove}
                    onPointerUp={handleOverlayPointerUp}
                    onPointerCancel={handleOverlayPointerUp}
                    sx={{
                      touchAction: 'none',
                      cursor: 'grab',
                      userSelect: 'none',
                      ...(current.type === 'image' && frameWidth ? { fontSize: frameWidth * OVERLAY_FONT_RATIO } : {}),
                    }}
                  />
                ))}
              </Box>
            </Box>

            {/* Archivos elegidos */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0, overflowX: 'auto', boxSizing: 'border-box', width: '100%', maxWidth: 600, mx: 'auto', px: 1, py: 1, scrollbarWidth: 'none' }}>
              {items.map((item) => (
                <Box key={item.id} sx={{ position: 'relative', width: 52, height: 52, flexShrink: 0 }}>
                  <Box
                    component={item.type === 'video' ? 'video' : 'img'}
                    src={item.preview}
                    onClick={() => setCurrentId(item.id)}
                    sx={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover', cursor: 'pointer', borderRadius: '10px', boxSizing: 'border-box', border: item.id === current.id ? '2px solid #00d4ff' : '2px solid transparent', bgcolor: '#222' }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => removeItem(item.id)}
                    sx={{ position: 'absolute', top: -2, right: -2, p: 0.1, bgcolor: 'rgba(0,0,0,0.75)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.9)' } }}
                  >
                    <Close sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ))}
              {items.length < MAX_ITEMS && (
                <IconButton onClick={() => galleryInputRef.current?.click()} sx={{ flexShrink: 0, width: 52, height: 52, borderRadius: '10px', color: '#00d4ff', border: '1px dashed rgba(0,212,255,0.5)' }}>
                  <Add />
                </IconButton>
              )}
              {items.length > 1 && (
                <>
                  <IconButton onClick={() => moveItem(current.id, -1)} sx={{ color: '#fff', flexShrink: 0 }}><ChevronLeft /></IconButton>
                  <IconButton onClick={() => moveItem(current.id, 1)} sx={{ color: '#fff', flexShrink: 0 }}><ChevronRight /></IconButton>
                </>
              )}
            </Stack>

            {/* Herramientas */}
            <Box sx={{ flexShrink: 0, boxSizing: 'border-box', width: '100%', maxWidth: 600, mx: 'auto', px: 1, pb: 1, color: '#fff' }}>
              <Box sx={{ minHeight: 56, maxHeight: 180, overflowY: 'auto', py: 0.75 }}>{toolPanels[tool]}</Box>
              <Stack direction="row" justifyContent="space-around">
                {tools.map(({ id, labelKey, Icon }) => (
                  <Button
                    key={id}
                    onClick={() => setActiveTool(id)}
                    sx={{ flexDirection: 'column', flex: 1, minWidth: 0, gap: 0.25, textTransform: 'none', fontSize: '0.7rem', color: tool === id ? '#00d4ff' : '#fff' }}
                  >
                    <Icon fontSize="small" />
                    {t(labelKey)}
                  </Button>
                ))}
              </Stack>
            </Box>
          </>
        )}

        {/* Paso 3: detalles */}
        {step === 'details' && (
          <Box sx={{ flex: 1, overflowY: 'auto', boxSizing: 'border-box', width: '100%', maxWidth: 600, mx: 'auto', px: 2, py: 1 }}>
            {mode === 'media' && (
              <>
                <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 1, scrollbarWidth: 'none' }}>
                  {items.map((item) => (
                    <Box key={item.id} sx={{ position: 'relative', width: 72, height: 72, flexShrink: 0 }}>
                      <Box component={item.type === 'video' ? 'video' : 'img'} src={item.preview} sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '10px', display: 'block', bgcolor: '#222', filter: buildFilterCss(item.edit.filter, item.edit.adjust) }} />
                      {loading && uploadProgress[item.id] !== undefined && uploadProgress[item.id] < 100 && (
                        <Box sx={{ position: 'absolute', inset: 0, borderRadius: '10px', bgcolor: 'rgba(0,0,0,0.5)', display: 'grid', placeItems: 'center' }}>
                          <CircularProgress variant="determinate" value={uploadProgress[item.id]} size={30} sx={{ color: '#00d4ff' }} />
                        </Box>
                      )}
                    </Box>
                  ))}
                </Stack>
                <Stack direction="row" spacing={1.5} sx={{ mb: 1 }}>
                  <Avatar src={userPhoto} sx={{ width: 34, height: 34 }} />
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={5}
                    variant="standard"
                    placeholder={t('captionPlaceholder')}
                    value={text}
                    disabled={loading}
                    onChange={(e) => setText(e.target.value.slice(0, MAX_TEXT))}
                    InputProps={{ disableUnderline: true, sx: { color: onSurface(1) } }}
                  />
                </Stack>
              </>
            )}

            <PostDetailsFields details={details} disabled={loading} isLightTheme={isLightTheme} />

            {loading && items.length > 0 && (
              <LinearProgress
                variant="determinate"
                value={overallProgress}
                sx={{ mt: 1, height: 6, borderRadius: '999px', bgcolor: onSurface(0.08), '& .MuiLinearProgress-bar': { bgcolor: '#00d4ff' } }}
              />
            )}
            <Button
              onClick={handlePost}
              disabled={loading || !canPublish}
              variant="contained"
              fullWidth
              endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Send />}
              sx={{ mt: 1.5, mb: 2, borderRadius: '12px', fontWeight: 700, textTransform: 'none', background: 'linear-gradient(45deg, #00d4ff 30%, #0045ff 90%)' }}
            >
              {loading ? `${t('publishing')}${items.length > 0 ? ` ${overallProgress}%` : ''}` : t('publish')}
            </Button>
          </Box>
        )}

        <Dialog
          open={confirmDiscard}
          onClose={() => setConfirmDiscard(false)}
          fullWidth
          maxWidth="xs"
          PaperProps={{ sx: { bgcolor: isLightTheme ? '#fff' : '#141414', backgroundImage: 'none', color: onSurface(1), borderRadius: '18px' } }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>{t('discardTitle')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: onSurface(0.75) }}>{t('discardWarning')}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDiscard(false)} sx={{ color: onSurface(1), textTransform: 'none' }}>{t('keepEditing')}</Button>
            <Button onClick={handleClose} sx={{ color: '#ff6b6b', textTransform: 'none', fontWeight: 700 }}>{t('discard')}</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Modal>
  );
};

export default Publicar;
